import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, Upload, Trash2, Plus, Loader2,
  Play, Save, ShieldCheck, DollarSign, AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /admin/gifts — admin uploads MP4 boost animations and maps them to
 * dollar amounts. The takeover on VideoView plays the highest-matching
 * MP4 for the tip amount; if none matches, falls back to the built-in
 * CSS/Lottie tiers.
 */
export default function GiftEditorAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    api
      .get("/admin/gifts/animations")
      .then((r) => {
        const list = Array.isArray(r.data?.tiers) ? r.data.tiers : [];
        setTiers(list.length ? list : DEFAULT_ROWS);
      })
      .catch((e) => {
        setError(
          e?.response?.status === 403
            ? "This dashboard is admin-only"
            : e?.response?.data?.detail || "Failed to load"
        );
      })
      .finally(() => setInitialLoad(false));
  }, [authLoading]);

  const hasChanges = useMemo(() => true, [tiers]);

  const updateTier = (idx, patch) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const removeTier = (idx) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const addTier = () => {
    setTiers((prev) => [
      ...prev,
      { min_cents: 0, label: "", video_url: "" },
    ]);
  };

  const handleSave = async () => {
    // Basic client-side validation — backend also enforces.
    const cleaned = tiers
      .filter((t) => Number(t.min_cents) > 0 && String(t.video_url || "").trim())
      .map((t) => ({
        min_cents: Number(t.min_cents),
        label: String(t.label || "").trim(),
        video_url: String(t.video_url).trim(),
        size: Number(t.size || 0),
      }));
    if (cleaned.length === 0) {
      toast.error("Add at least one tier with an amount and an MP4");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/gifts/animations", { tiers: cleaned });
      const saved = r.data?.tiers || cleaned;
      setTiers(saved);
      toast.success(`Saved ${saved.length} boost animation${saved.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // NOTE: we intentionally do NOT gate on `user.is_admin` client-side.
  // The backend `require_admin` dep is the single source of truth. Client
  // state can lag or drift (mobile Safari occasionally holds a stale user
  // object across route transitions), and blocking on client state made
  // valid admins hit "Admin only" while the sidebar simultaneously
  // rendered admin links. If the API call succeeds we render; if it
  // fails with 403 we show the reject screen from the error path.
  if (authLoading || initialLoad) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF007F]" />
      </div>
    );
  }
  if (error) {
    return <AdminOnly message={error} currentUser={user} />;
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      {/* Header */}
      <div className="border-b border-white/10 bg-gradient-to-b from-black to-black/95 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/" data-testid="gift-editor-back" className="p-2 hover:bg-white/5 rounded">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <p className="font-display text-lg font-black tracking-wide flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#FFC300]" />
              BOOST ANIMATIONS
            </p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              admin · upload mp4 per dollar amount
            </p>
          </div>
          <button
            data-testid="gift-editor-save"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-[#FFC300] text-black font-mono text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#FFC300]/90 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? "saving" : "save"}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-6">
        {/* How-it-works */}
        <div className="border border-[#FFC300]/30 bg-[#FFC300]/5 p-4 mb-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#FFC300] mb-1">
            // how.it.works
          </p>
          <p className="text-sm text-white/80 leading-relaxed">
            When a viewer boosts a video, we play the <b>highest-matching</b> MP4 as a full-screen takeover.
            Example: with tiers set at $2, $4, $6, $7, $8, $9 — a $6.50 boost plays the $6 MP4; a $9+ boost plays the $9 MP4.
            No MP4 matches? Falls back to the built-in CSS animation.
          </p>
          <p className="text-xs text-white/50 mt-2 font-mono">
            · MP4 / WebM / MOV · max 25MB · plays once, fades out · muted by default
          </p>
        </div>

        {/* Tier rows */}
        <div className="space-y-3">
          {tiers.map((t, idx) => (
            <TierRow
              key={idx}
              index={idx}
              tier={t}
              onChange={(patch) => updateTier(idx, patch)}
              onRemove={() => removeTier(idx)}
            />
          ))}
        </div>

        <button
          data-testid="gift-editor-add-tier"
          onClick={addTier}
          className="mt-4 flex items-center gap-2 border border-dashed border-white/20 hover:border-[#FFC300]/60 hover:bg-[#FFC300]/5 px-4 py-3 w-full justify-center font-mono text-xs uppercase tracking-widest text-white/60 hover:text-[#FFC300] transition-colors"
        >
          <Plus className="h-4 w-4" /> add.tier
        </button>
      </div>
    </div>
  );
}

// Default rows to bootstrap the editor when nothing is saved yet — matches
// the user's requested dollar amounts ($2, $4, $6, $7, $8, $9).
const DEFAULT_ROWS = [
  { min_cents: 200, label: "TIER $2", video_url: "" },
  { min_cents: 400, label: "TIER $4", video_url: "" },
  { min_cents: 600, label: "TIER $6", video_url: "" },
  { min_cents: 700, label: "TIER $7", video_url: "" },
  { min_cents: 800, label: "TIER $8", video_url: "" },
  { min_cents: 900, label: "TIER $9", video_url: "" },
];

function TierRow({ index, tier, onChange, onRemove }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const amountUsd = ((Number(tier.min_cents) || 0) / 100).toFixed(2);
  const sizeBytes = Number(tier.size || 0);
  // 5MB threshold — anything larger takes >1s to cold-fetch on 4G and
  // starts to stutter on entry-level Android devices. We surface a
  // warning so admins compress before shipping to their audience.
  const WARN_BYTES = 5 * 1024 * 1024;
  const oversize = sizeBytes > WARN_BYTES;
  const sizeLabel = sizeBytes > 0
    ? (sizeBytes >= 1024 * 1024
        ? `${(sizeBytes / (1024 * 1024)).toFixed(1)}MB`
        : `${Math.round(sizeBytes / 1024)}KB`)
    : null;

  // Fires the same CustomEvent GiftShowcase listens on, so the takeover
  // plays exactly like a real boost — no server hit needed.
  const handlePreview = () => {
    const cents = Number(tier.min_cents) || 0;
    if (cents <= 0) {
      toast.error("Set an amount first");
      return;
    }
    if (!String(tier.video_url || "").trim()) {
      toast.error("Upload an MP4 first");
      return;
    }
    // Persist to server so GiftShowcase's refreshCustom() finds this URL —
    // otherwise a still-unsaved row won't be in the /gifts/tiers response.
    window.dispatchEvent(new CustomEvent("neosync:gift-preview", {
      detail: {
        id: `preview-${Date.now()}`,
        amount_cents: cents,
        tipper_name: "Preview",
        message: tier.label || "boost preview",
        // Inline the URL so this works even if the tier hasn't been saved
        // yet — Takeover prefers this over the fetched catalog match.
        preview_video_url: tier.video_url,
      },
    }));
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please pick a video file (MP4/WebM/MOV)");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (25MB max)");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/gifts/animations/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      const size = Number(r.data?.size || file.size || 0);
      if (!url) throw new Error("No URL returned");
      onChange({ video_url: url, size });
      toast.success("Animation uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      data-testid={`tier-row-${index}`}
      className="border border-white/10 bg-white/[0.02] p-4 flex flex-col sm:flex-row gap-4 items-start"
    >
      {/* Amount */}
      <div className="flex flex-col gap-1">
        <label className="font-mono text-[9px] uppercase tracking-widest text-white/40">
          amount
        </label>
        <div className="flex items-center border border-white/10 focus-within:border-[#FFC300]/70">
          <DollarSign className="h-4 w-4 text-white/40 ml-2" />
          <input
            data-testid={`tier-amount-${index}`}
            type="number"
            min="0.01"
            step="0.01"
            value={amountUsd}
            onChange={(e) => {
              const v = Math.round(Number(e.target.value || 0) * 100);
              onChange({ min_cents: v });
            }}
            className="bg-transparent px-2 py-1.5 w-20 outline-none font-mono text-sm"
          />
        </div>
      </div>

      {/* Label */}
      <div className="flex flex-col gap-1 flex-1 min-w-[140px] w-full sm:w-auto">
        <label className="font-mono text-[9px] uppercase tracking-widest text-white/40">
          label (optional)
        </label>
        <input
          data-testid={`tier-label-${index}`}
          type="text"
          maxLength={40}
          placeholder="e.g. GOLD RUSH"
          value={tier.label || ""}
          onChange={(e) => onChange({ label: e.target.value })}
          className="bg-transparent border border-white/10 focus:border-[#FFC300]/70 px-3 py-1.5 outline-none font-mono text-sm w-full"
        />
      </div>

      {/* Video preview + upload */}
      <div className="flex flex-col gap-1 flex-1 w-full sm:w-auto">
        <label className="font-mono text-[9px] uppercase tracking-widest text-white/40">
          mp4 preview
        </label>
        <div className="flex items-center gap-2">
          {tier.video_url ? (
            <video
              data-testid={`tier-preview-${index}`}
              src={tier.video_url}
              muted
              playsInline
              loop
              autoPlay
              className="h-14 w-14 object-cover border border-[#FFC300]/40 bg-black"
            />
          ) : (
            <div className="h-14 w-14 border border-dashed border-white/20 flex items-center justify-center">
              <Play className="h-4 w-4 text-white/30" />
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/*"
            data-testid={`tier-file-${index}`}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />
          <button
            data-testid={`tier-upload-${index}`}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 border border-white/20 hover:border-[#FFC300]/60 hover:bg-[#FFC300]/5 px-3 py-2 font-mono text-[10px] uppercase tracking-widest disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {uploading ? "uploading" : tier.video_url ? "replace" : "upload"}
          </button>
        </div>
        {/* Size + oversize warning. Shown only after upload so admins get
            immediate feedback if their file is too heavy for mobile. */}
        {sizeLabel && (
          <div
            data-testid={`tier-size-${index}`}
            className={`mt-1 flex items-center gap-1.5 font-mono text-[10px] ${
              oversize ? "text-[#FF007F]" : "text-white/40"
            }`}
          >
            {oversize && <AlertTriangle className="h-3 w-3" />}
            <span>{sizeLabel}</span>
            {oversize && (
              <span className="uppercase tracking-widest">
                · large · compress to speed up boosts
              </span>
            )}
          </div>
        )}
      </div>

      {/* Preview + Remove */}
      <div className="flex flex-col items-end gap-2 mt-6">
        <button
          data-testid={`tier-preview-btn-${index}`}
          onClick={handlePreview}
          disabled={!tier.video_url}
          title="Play the takeover animation without sending a real boost"
          className="flex items-center gap-1.5 border border-[#39FF14]/50 hover:bg-[#39FF14]/10 disabled:opacity-30 disabled:cursor-not-allowed px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-[#39FF14]"
        >
          <Play className="h-3 w-3" fill="currentColor" /> preview
        </button>
        <button
          data-testid={`tier-remove-${index}`}
          onClick={onRemove}
          title="Remove tier"
          className="p-2 text-white/40 hover:text-[#FF007F] transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function AdminOnly({ message, currentUser }) {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 text-center">
      <ShieldCheck className="h-12 w-12 text-[#FF007F] mb-4" />
      <p className="font-display font-bold text-2xl mb-2">Admin only</p>
      <p className="font-mono text-xs uppercase tracking-widest text-white/50 max-w-sm">
        {message || "You do not have permission to view this dashboard."}
      </p>
      {currentUser && (
        <div className="mt-6 border border-white/10 bg-white/[0.03] px-4 py-3 max-w-sm w-full">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
            // signed.in.as
          </p>
          <p className="text-sm font-semibold">{currentUser.name || currentUser.username}</p>
          <p className="font-mono text-xs text-white/50">@{currentUser.username}</p>
          <p className="text-[11px] text-white/40 mt-2 leading-relaxed">
            If this isn&apos;t your admin account, sign out and log back in with the right one. Preview and production have separate sessions.
          </p>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {currentUser && (
          <button
            data-testid="admin-only-signout"
            onClick={async () => { await logout?.(); window.location.href = "/login"; }}
            className="font-mono text-xs uppercase tracking-widest text-[#FF007F] border border-[#FF007F]/50 hover:bg-[#FF007F]/10 px-4 py-2"
          >
            sign out
          </button>
        )}
        <Link
          to="/"
          className="font-mono text-xs uppercase tracking-widest text-[#00FFFF] border border-[#00FFFF]/50 hover:bg-[#00FFFF]/10 px-4 py-2"
        >
          return home
        </Link>
      </div>
    </div>
  );
}
